import {inject, Injectable} from '@angular/core';
import {SettingsService} from '@gatewee/services/core/settings.service';
import {PaymentReceipt} from '@gatewee/types/payment';
import {PublicSettings} from '@gatewee/types/system';
import {Withdrawal, WithdrawalStatus} from '@gatewee/types/withdrawal';
import {
  formatRecipientAccount,
  formatRecipientAgency,
  resolveCompeDisplayName,
  resolveWithdrawalAmounts,
} from '@gatewee/utils';
import {TranslateService} from '@ngx-translate/core';
import {PaymentReceiptExportModel} from './payment-receipt-export.model';
import {PaymentReceiptExportService} from './payment-receipt-export.service';

@Injectable({providedIn: 'root'})
export class WithdrawalReceiptExportService {
  private readonly paymentReceiptExport = inject(PaymentReceiptExportService);
  private readonly settingsService = inject(SettingsService);
  private readonly translate = inject(TranslateService);

  canDownload(withdrawal: Withdrawal | null | undefined): boolean {
    if (!withdrawal) {
      return false;
    }
    return (
      withdrawal.status === WithdrawalStatus.SUCCESS &&
      !!withdrawal.paymentMetadata?.receipt
    );
  }

  receiptFilename(receipt: PaymentReceipt, isBankSlip = false): string {
    const idPart = String(receipt.e2eId ?? receipt.id ?? 'receipt').replace(
      /[^\w.-]+/g,
      '_',
    );
    const prefix = isBankSlip ? 'comprovante-boleto' : 'comprovante-pix';
    return `${prefix}-${idPart}.png`;
  }

  async download(withdrawal: Withdrawal): Promise<void> {
    const receipt = withdrawal.paymentMetadata?.receipt;
    if (!this.canDownload(withdrawal) || !receipt) {
      return;
    }
    const model = this.buildModel(withdrawal, receipt);
    await this.paymentReceiptExport.downloadPng(
      model,
      this.receiptFilename(receipt, !!model.isBankSlip),
    );
  }

  buildModel(
    withdrawal: Withdrawal,
    receipt: PaymentReceipt,
  ): PaymentReceiptExportModel {
    const branding = this.settingsService.get('branding') as
      | PublicSettings['branding']
      | undefined;
    const style = branding?.style;
    const breakdown = resolveWithdrawalAmounts(withdrawal);
    const digitableLine = this.resolveDigitableLine(withdrawal, receipt);
    const isBankSlip = !!digitableLine;

    return {
      receipt,
      amount: breakdown?.amount ?? withdrawal.amount ?? null,
      requestedAmount: breakdown?.amount ?? withdrawal.amount ?? null,
      calculatedFee: breakdown?.calculatedFee ?? null,
      finalAmount: breakdown?.finalAmount ?? null,
      recipientDisplayName: this.resolveRecipientDisplayName(
        withdrawal,
        receipt,
      ),
      bankDisplayLine: this.resolveBankDisplayLine(receipt, isBankSlip),
      providerLabel: this.resolveProviderLabel(withdrawal),
      agencyDisplay: formatRecipientAgency(receipt.receiver?.agency),
      accountDisplay: formatRecipientAccount(receipt.receiver?.account),
      digitableLine,
      isBankSlip,
      logoUrl: style?.logoBlack || style?.logo || null,
      brandPrimaryColor: style?.primaryColor || '#ff5c00',
      domainTitle: branding?.page?.title?.trim() ?? '',
    };
  }

  private resolveDigitableLine(
    withdrawal: Withdrawal,
    receipt: PaymentReceipt,
  ): string {
    const fromReceipt = String(receipt.digitableLine ?? '').trim();
    if (fromReceipt) {
      return fromReceipt;
    }
    return String(withdrawal.receiverPaymentInfo?.digitableLine ?? '').trim();
  }

  private resolveRecipientDisplayName(
    withdrawal: Withdrawal,
    receipt: PaymentReceipt,
  ): string {
    const fromReceipt = String(receipt.receiver?.holderName ?? '').trim();
    if (fromReceipt) {
      return fromReceipt;
    }
    const info = withdrawal.receiverPaymentInfo;
    const fromInfo = String(info?.holderName ?? info?.name ?? '').trim();
    if (fromInfo) {
      return fromInfo;
    }
    return this.translate.instant(
      'features.withdrawals.modalPix.recipientNameUnavailable',
    );
  }

  private resolveBankDisplayLine(
    receipt: PaymentReceipt,
    isBankSlip: boolean,
  ): string {
    const line = resolveCompeDisplayName(
      receipt.receiver?.compeCode,
      receipt.receiver?.ispbCode,
    ).trim();
    if (line) {
      return line;
    }
    if (isBankSlip) {
      return '';
    }
    return this.translate.instant(
      'features.withdrawals.modalPix.valueUnavailable',
    );
  }

  private resolveProviderLabel(withdrawal: Withdrawal): string {
    const provider = withdrawal.paymentMetadata?.provider;
    if (provider === null || provider === undefined) {
      return '';
    }
    const slug = String(provider).trim().toLowerCase();
    if (!slug) {
      return '';
    }
    const key = `enums.paymentProvider.${slug}`;
    const translated = this.translate.instant(key);
    if (!translated || translated === key) {
      return this.translate.instant('common.unknown');
    }
    return translated;
  }
}
